import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import { GoogleMap, useJsApiLoader, Marker, OverlayView } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getRestaurants } from '../../services/firebaseData';
import { mapStyles } from '../../utils/mapStyles';
import SearchBar from '../utilities/SearchBar';
import { SurfaceCard } from '../ui/ConvivialComponents';
import { getConvivialNativeLocation } from '../../services/nativeLocation';
import { requestNativeLogin } from '../../services/nativeAppBridge';
import { getPlaceCity, getTableCity, isTableFull } from '../../utils/tablePresentation';

const GOOGLE_MAP_LIBRARIES = ['places', 'geometry'];
const centerMapIcon = (width, height) => ({ x: -(width / 2), y: -(height / 2) });

function getTodayOpeningHours(openingHours) {
    if (!Array.isArray(openingHours) || openingHours.length === 0) return '';
    const today = new Date().getDay();
    const googleWeekdayIndex = today === 0 ? 6 : today - 1;
    return openingHours[googleWeekdayIndex] || openingHours[0] || '';
}

function buildVenueCTA(place, searchedPlace, fallbackPlace = {}) {
    const placeId = place.place_id || fallbackPlace.placeId || fallbackPlace.place_id || '';
    const photo = searchedPlace.photo || fallbackPlace.photo || '';
    return {
        id: placeId,
        type: 'VENUE',
        name: place.name || fallbackPlace.name || fallbackPlace.label || '',
        location: searchedPlace,
        address: place.formatted_address || fallbackPlace.address || searchedPlace.directions || '',
        placeId,
        phone: place.international_phone_number || '',
        openingHours: place.opening_hours?.weekday_text || fallbackPlace.openingHours || [],
        photo,
    };
}

export default function IndexMap({ latLon, setLatLon, pins, iconSpinning, currentLocation }) {
    const [restaurants, setRestaurants] = useState([]);
    useEffect(() => {
        let active = true;
        getRestaurants().then((data) => { if (active) setRestaurants(data); })
            .catch((error) => console.warn('Caricamento ristoranti non riuscito:', error));
        return () => { active = false; };
    }, []);
    const [map, setMap] = useState(null);
    const [center, setCenter] = useState(latLon);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentCTA, setCurrentCTA] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [userLocation, setUserLocation] = useState(currentLocation || null);
    const navigate = useNavigate();
    const theme = useTheme();
    const userId = localStorage.getItem('user_id');

    const goToCreateTable = () => {
        if (!userId) {
            const handledByNative = requestNativeLogin('/table');

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo: '/table' } });
            }
            return;
        }
        navigate('/table');
    };

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
        libraries: GOOGLE_MAP_LIBRARIES,
        language: 'it',
    });

    const focusMapTarget = useCallback((newCenter, zoom = 16) => {
        if (!newCenter?.lat || !newCenter?.lng) return;

        const normalizedCenter = {
            lat: Number(newCenter.lat),
            lng: Number(newCenter.lng),
        };

        setCenter(normalizedCenter);
        setLatLon(normalizedCenter);

        if (map) {
            map.panTo(normalizedCenter);
            map.setZoom(zoom);
        }
    }, [map, setLatLon]);

    const handleGeolocationClick = () => {
        const nativeLocation = getConvivialNativeLocation();

        if (nativeLocation) {
            const newCenter = {
                lat: nativeLocation.lat,
                lng: nativeLocation.lng,
            };

            setUserLocation(newCenter);
            focusMapTarget(newCenter, 16);
            return;
        }

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newCenter = {
                        lat: Number(position.coords.latitude),
                        lng: Number(position.coords.longitude),
                    };
                    setUserLocation(newCenter);
                    focusMapTarget(newCenter, 16);
                },
                () => {
                    alert('Non riusciamo ad ottenere la posizione, controlla le tue impostazioni');
                }
            );
        } else {
            alert('Geolocalizzazione non supportata');
        }
    };


    const handleSearchSelection = async (selectedPlace) => {
        const service = new window.google.maps.places.PlacesService(map || document.createElement('div'));
        service.getDetails(
            {
                placeId: selectedPlace.placeId,
                fields: [
                    "place_id",
                    "name",
                    "formatted_address",
                    "address_components",
                    "geometry",
                    "international_phone_number",
                    "opening_hours",
                    "photos",
                ],
            },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {

                    const newCenter = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                    };

                    const searchedPlace = {
                        lat: newCenter.lat,
                        lng: newCenter.lng,
                        place: place.name || "",
                        directions: place.formatted_address || "",
                        city: getPlaceCity(place),
                        phone: place.international_phone_number || "",
                        photo: place.photos?.[0]?.getUrl({ maxWidth: 1200, maxHeight: 700 }) || selectedPlace.photo || '',
                    };

                    setCurrentCTA(buildVenueCTA(place, searchedPlace, selectedPlace));

                    focusMapTarget(newCenter, 20);

                    setSearchQuery(place.name || "");
                    localStorage.setItem("lastQueryPlace", place.name || "");
                    localStorage.setItem("searchedPlace", JSON.stringify(searchedPlace));

                    console.log('searchplace 1', searchedPlace);
                }
            }
        );
    };

    const handleMapClick = () => {
        if (currentCTA) {
            setCurrentCTA(null);
        }
    };

    // useEffect(() => {
    //     if (map) {
    //         map.panTo(latLon);
    //         if (map.getZoom() > 19) {
    //             setCurrentCTA('restaurant')
    //         } else {
    //             setCurrentCTA(false);
    //         }
    //     }
    // }, [latLon, map]);

    useEffect(() => {
        if (!latLon?.lat || !latLon?.lng) return;

        const nextCenter = {
            lat: Number(latLon.lat),
            lng: Number(latLon.lng),
        };

        if (
            !center ||
            Math.abs(Number(center.lat) - nextCenter.lat) > 0.000001 ||
            Math.abs(Number(center.lng) - nextCenter.lng) > 0.000001
        ) {
            setCenter(nextCenter);
            if (map) map.panTo(nextCenter);
        }
    }, [center, latLon, map]);

    useEffect(() => {
        if (currentLocation?.lat && currentLocation?.lng) {
            setUserLocation({
                lat: Number(currentLocation.lat),
                lng: Number(currentLocation.lng),
            });
        }
    }, [currentLocation]);

    useEffect(() => {
        if (!map) return;

        const listener = map.addListener('click', function (event) {
            if (event.placeId) {
                event.stop(); // Prevent default Google info window

                // Use the PlacesService to get details
                const service = new window.google.maps.places.PlacesService(map);
                service.getDetails(
                    {
                        placeId: event.placeId,
                        fields: [
                            "place_id",
                            "name",
                            "formatted_address",
                            "address_components",
                            "geometry",
                            "international_phone_number",
                            "opening_hours",
                            "photos",
                        ],
                    },
                    (place, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK) {

                            const newCenter = {
                                lat: place.geometry.location.lat(),
                                lng: place.geometry.location.lng(),
                            };

                            const searchedPlace = {
                                lat: newCenter.lat,
                                lng: newCenter.lng,
                                place: place.name || "",
                                directions: place.formatted_address || "",
                                city: getPlaceCity(place),
                                phone: place.international_phone_number || "",
                                photo: place.photos?.[0]?.getUrl({ maxWidth: 1200, maxHeight: 700 }) || '',
                            };

                            setCurrentCTA(buildVenueCTA(place, searchedPlace));

                            focusMapTarget(newCenter, 20);

                            setSearchQuery(place.name || "");
                            localStorage.setItem("lastQueryPlace", place.name || "");
                            localStorage.setItem("searchedPlace", JSON.stringify(searchedPlace));

                            console.log('searchplace 2', searchedPlace);

                        }
                    }
                );
            }
        });

        // Cleanup on unmount
        return () => window.google.maps.event.removeListener(listener);
    }, [focusMapTarget, map]);

    const syncMapCenter = useCallback(() => {
        if (!map) return;

        const mapCenter = map.getCenter();
        if (!mapCenter) return;

        const newCenter = {
            lat: mapCenter.lat(),
            lng: mapCenter.lng(),
        };

        if (
            Math.abs(Number(center?.lat) - newCenter.lat) < 0.000001 &&
            Math.abs(Number(center?.lng) - newCenter.lng) < 0.000001
        ) {
            return;
        }

        setCenter(newCenter);
        setLatLon(newCenter);
    }, [center, map, setLatLon]);


    if (!latLon || !latLon.lat || !latLon.lng) {
        return <div>Loading...</div>;
    }

    if (!isLoaded) {
        return <Box sx={{ height: 'calc(100dvh - var(--cv-top-bar-height) - var(--cv-bottom-nav-height))', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>Caricamento mappa...</Box>;
    }

    return (
        <Box
            sx={{
                height: 'calc(100dvh - var(--cv-top-bar-height) - var(--cv-bottom-nav-height))',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    width: 'calc(100% - 24px)',
                    maxWidth: 560,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Box>
                    <SearchBar
                        center={center}
                        onSearchSelection={handleSearchSelection}
                        onGeolocation={handleGeolocationClick}
                        iconSpinning={iconSpinning}
                        textInside={searchQuery}
                    />
                </Box>
            </Box>

            {/* Map */}
            <GoogleMap
                center={center}
                zoom={16}
                onLoad={(mapInstance) => setMap(mapInstance)}
                onClick={handleMapClick}
                onDragEnd={syncMapCenter}
                onIdle={syncMapCenter}
                mapContainerStyle={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                }}
                options={{
                    styles: mapStyles,
                    clickableIcons: true,
                    backgroundColor: '#f1eee7',
                    gestureHandling: 'greedy',
                    fullscreenControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                }}
            >
                {Array.isArray(pins) && pins.filter(pin => pin.type === 'TABLE').map((pin) => {
                    const location = pin.location || {};
                    let latLng = { lat: parseFloat(location.lat), lng: parseFloat(location.lng) };
                    const isFull = isTableFull(pin);

                    let pinSizes = 50;
                    let pinImageUrl = 'https://www.convivialapp.it/public/buble_convivial.png';
                    const handlePinClick = () => {
                        const location_info = pin.location || {};

                        const newCenter = {
                            lat: Number(location_info.lat),
                            lng: Number(location_info.lng),
                        };

                        const searchedPlace = {
                            lat: newCenter.lat,
                            lng: newCenter.lng,
                            place: location_info.place,
                            directions: location_info.directions,
                            city: getTableCity(pin),
                            phone: location_info.phone || '',
                        };

                        setCurrentCTA(pin);
                        focusMapTarget(newCenter, 20);
                        setSearchQuery(pin.name || "");
                        localStorage.setItem("lastQueryPlace", pin.name || "");
                        localStorage.setItem("searchedPlace", JSON.stringify(searchedPlace));
                    };

                    if (isFull) {
                        return (
                            <OverlayView
                                key={pin.id}
                                position={latLng}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                getPixelPositionOffset={centerMapIcon}
                            >
                                <Box
                                    component="img"
                                    src={pinImageUrl}
                                    alt="Tavolo completo"
                                    onClick={handlePinClick}
                                    sx={{
                                        display: 'block',
                                        width: pinSizes,
                                        height: pinSizes,
                                        cursor: 'pointer',
                                        filter: 'grayscale(1)',
                                        opacity: 1,
                                    }}
                                />
                            </OverlayView>
                        );
                    }

                    return (
                        <Marker
                            key={pin.id}
                            position={latLng}
                            onClick={handlePinClick}
                            icon={{
                                url: pinImageUrl,
                                scaledSize: new window.google.maps.Size(pinSizes, pinSizes),
                                anchor: new window.google.maps.Point(pinSizes / 2, pinSizes / 2),
                            }}
                            options={{
                                zIndex: 2,
                                shape: { type: 'circle', coords: [pinSizes / 2, pinSizes / 2, pinSizes / 2] }
                            }}
                        />
                    );
                })}
                {restaurants.map((restaurant) => (
                    <OverlayView key={restaurant.id} position={restaurant.location}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centerMapIcon}>
                        <Button aria-label={`Ristorante aderente: ${restaurant.name}`}
                            onClick={() => navigate(`/restaurants/${restaurant.id}`)}
                            sx={{ minWidth: 50, width: 50, height: 50, p: 0,
                                '&:hover': { bgcolor: 'transparent', transform: 'scale(1.05)' } }}>
                            <Box component="img" src={`${process.env.PUBLIC_URL}/assets/restaurant-pin-building.png`}
                                alt="" sx={{ width: 50, height: 50, objectFit: 'contain' }} />
                        </Button>
                    </OverlayView>
                ))}
                {userLocation && (
                    <Marker
                        position={userLocation}
                        zIndex={1000}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: theme.palette.secondary.main,
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 3,
                        }}
                    />
                )}
            </GoogleMap>

            {currentCTA && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 18,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#fff',
                        padding: 2.5,
                        borderRadius: 4,
                        boxShadow: '0 10px 30px rgba(29, 29, 27, 0.12)',
                        border: '1px solid rgba(223, 191, 193, 0.45)',
                        width: '90%',
                        maxWidth: 400,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {typeof currentCTA === 'object' ? (
                        <>
                            {currentCTA.type === 'TABLE' ? (
                                <>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            marginBottom: 1,
                                            textAlign: 'center',
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {currentCTA.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            marginBottom: 1,
                                            fontSize: '0.9rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <strong>Giorno:</strong>{' '}
                                        {new Date(currentCTA.startAt).toLocaleDateString('it-IT', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                        })}
                                        <br />
                                        <strong>Orario:</strong>{' '}
                                        {new Date(currentCTA.startAt).toLocaleTimeString('it-IT', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}{' '}
                                        -{' '}
                                        {new Date(currentCTA.endAt).toLocaleTimeString('it-IT', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            marginBottom: 1,
                                            fontSize: '0.9rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <strong>Posti disponibili:</strong>{' '}
                                        {currentCTA.limit - currentCTA.attendeeCount} posti rimasti
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            borderRadius: 3,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                            },
                                        }}
                                        onClick={() => navigate(`/table/${currentCTA.id}`)}
                                    >
                                        Dettagli Tavolo
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {currentCTA.photo && (
                                        <Box
                                            component="img"
                                            src={currentCTA.photo}
                                            alt={currentCTA.name}
                                            sx={{
                                                width: '100%',
                                                height: 130,
                                                objectFit: 'cover',
                                                borderRadius: 3,
                                                mb: 1.5,
                                            }}
                                        />
                                    )}
                                    <Typography variant="h3" sx={{ color: 'primary.main', textAlign: 'center', mb: 0.5 }}>
                                        {currentCTA.name || 'Locale selezionato'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1 }}>
                                        {currentCTA.address || currentCTA.location?.directions || 'Indirizzo non disponibile'}
                                    </Typography>
                                    {currentCTA.openingHours?.length > 0 && (
                                        <Box sx={{ width: '100%', bgcolor: 'background.surfaceLow', borderRadius: 2, p: 1.25, mb: 1.5 }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
                                                Orari
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                                                {getTodayOpeningHours(currentCTA.openingHours) || currentCTA.openingHours[0]}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            borderRadius: 3,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                            },
                                        }}
                                        onClick={() => {
                                            if (currentCTA.id) localStorage.setItem('tableVenue', currentCTA.id);
                                            goToCreateTable();
                                        }}
                                    >
                                        {userId ? 'Suggerisci un tavolo qui' : 'Accedi per suggerire'}
                                    </Button>
                                </>
                            )}
                        </>
                    ) : currentCTA === 'restaurant' ? (
                        <>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 'bold',
                                    marginBottom: 1,
                                    textAlign: 'center',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                Proponi un tavolo
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    marginBottom: 1,
                                    textAlign: 'center',
                                    fontSize: '0.9rem',
                                }}
                            >
                                {searchQuery}
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    borderRadius: 3,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                }}
                                onClick={() => {
                                    // Save venue info before navigating
                                    goToCreateTable();
                                }}
                            >
                                {userId ? 'Crea Tavolo' : 'Accedi per creare'}
                            </Button>
                        </>
                    ) : null}
                </Box>
            )}

            <Button
                aria-label="Come usare la mappa"
                onClick={() => setHelpOpen(true)}
                sx={{
                    position: 'absolute',
                    right: 18,
                    bottom: 18,
                    zIndex: 12,
                    minWidth: 0,
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.container',
                    color: '#fff',
                    boxShadow: '0 4px 14px rgba(97,0,31,0.18)',
                    '&:hover': { bgcolor: 'primary.main' },
                }}
            >
                <Box component="span" sx={{ fontSize: 28, fontWeight: 400, lineHeight: 1 }}>?</Box>
            </Button>
            <Modal open={helpOpen} onClose={() => setHelpOpen(false)}>
                <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'rgba(28,28,26,0.22)', backdropFilter: 'blur(8px)' }}>
                    <SurfaceCard sx={{ maxWidth: 420, width: '100%' }}>
                        <Typography variant="h3" sx={{ color: 'primary.main', mb: 1 }}>Come usare la mappa</Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                            Cerca una zona o un locale, esplora i tavoli sulla mappa e apri il dettaglio per inviare una richiesta di partecipazione.
                        </Typography>
                        <Stack spacing={1.25}>
                            <Button variant="contained" fullWidth onClick={() => setHelpOpen(false)} sx={{ borderRadius: 3, py: 1.4 }}>
                                Chiudi
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                    setHelpOpen(false);
                                    navigate('/more-info');
                                }}
                                sx={{ borderRadius: 3, py: 1.4, fontWeight: 800 }}
                            >
                                Come funziona Convivial
                            </Button>
                        </Stack>
                    </SurfaceCard>
                </Box>
            </Modal>

        </Box>
    );
}
